import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store/store';
import { AppDispatch } from '../../store/store';
import { ObjectiveDisplayMode, setRelativesViewMode } from '../../store/map3UISlice';
import { updateObjective } from '../../store/map3PendingSlice';
import { addResultDependency } from '../../store/map3PendingSlice';
import { useAllRelativesData } from '../hooks/useAllRelativesData';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { cloneDeep } from '../../utils/cloneDeep';

import { getDependencyId } from '@mm-shared/dependencies';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import type { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import type { Objective1 } from '@mm-shared/types/Objective1';
import type { DependencyRecord } from '@mm-shared/dependencies';
import type { Map3UISettings } from '../types';
import { Map3Relatives } from './Map3Relatives';
import { useComprehensiveDependencies } from '../hooks/useComprehensiveDependencies';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { filterMapObjectives } from '../utils/objectiveFiltering';
import { getOrderedObjectives } from '../../utils/objective-utils';

/**
 * Map3RelativesWrapper is responsible for connecting Map3Relatives to Redux and React Router.
 *
 * This wrapper component:
 * 1. Handles all Redux state management and dispatching
 * 2. Processes URL parameters and syncs them with Redux state
 * 3. Manages the throttled dispatch system for drag operations
 * 4. Provides memoized data to prevent unnecessary re-renders
 *
 * The separation of this wrapper from Map3Relatives.tsx follows the pattern of:
 * - Keeping Redux integration separate from UI components
 * - Making the data flow more explicit and maintainable
 * - Reducing the complexity of the main component
 *
 * @see Map3Relatives.tsx for the main component implementation
 */
export function Map3RelativesWrapper() {
  const { mapShortId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useDispatch<AppDispatch>();

  // Get viewMode from URL params first, then fall back to Redux state
  const urlViewMode = searchParams.get('viewMode') as 'stacked' | 'sharedTimeline' | null;
  const reduxViewMode = useSelector(
    (state: RootState) => state.map3UI.mapControls.relatives.viewMode,
  );

  // If URL has a viewMode, use it and update Redux state
  useEffect(() => {
    if (urlViewMode && urlViewMode !== reduxViewMode) {
      dispatch(setRelativesViewMode(urlViewMode));
    }
  }, [urlViewMode, reduxViewMode, dispatch]);

  // Use the viewMode from Redux state (which may have been updated by URL)
  const viewMode = urlViewMode || reduxViewMode || 'sharedTimeline';
  const zoomLevel = (searchParams.get('zoomLevel') as MilestoneMapInterval) || undefined;

  // Get visibility state from Redux (new state structure)
  const { addedMaps, relatedToVisibleMaps, visibleMapsDependencies, objectiveDisplayMode } =
    useSelector((state: RootState) => state.map3UI.mapControls.relatives);

  // Use hook to get up-to-date relatives data
  // TODO: Revisit.
  // - Should dependencies come from Redux now?
  // - Should this even still be a hook?
  const { loading, allRelatedMilestoneMaps } = useAllRelativesData(mapShortId || '');

  // Get comprehensive dependency data using centralized hook
  const { visibleDependencies, visibleMapUuids } =
    useComprehensiveDependencies(allRelatedMilestoneMaps);

  // Get all maps and objectives from Redux
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);
  const allObjectives: Objective1[] = useSelector((state: RootState) => state.map3Data.objectives);

  // Check user's role for edit permissions
  // TODO: Replace useParams with useCurrentMap to get the current map uuid.
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Find the main map by uuid or shortId (moved before visibleMaps to fix race condition)
  const mainMap = allRelatedMilestoneMaps.find((m) => getShortIdFromUuid(m.uuid) === mapShortId);
  const mainMilestoneMapUuid = mainMap ? mainMap.uuid : mapShortId || '';
  const effectiveZoomLevel = zoomLevel || mainMap?.interval;

  // For settings, get from Redux - extract only the properties needed for Map3UISettings
  const settings: Map3UISettings = useSelector((state: RootState) => ({
    showStatusCodes: state.map3UI.mapControls.showStatusCodes,
    enableCrosshairs: state.map3UI.mapControls.enableCrosshairs,
    enablePlaylistMode: state.map3UI.mapControls.enablePlaylistMode,
    toggleStatusCellDisplay: state.map3UI.mapControls.toggleStatusCellDisplay,
  }));

  // Filter maps based on visibility state
  // Always include the main map to prevent race condition crashes
  const visibleMaps = useMemo(() => {
    // Get the actual milestone map objects for visible UUIDs
    // This includes all maps (both related and manually added) since allMaps contains everything
    const maps = allMaps.filter((map) => visibleMapUuids.has(map.uuid));

    // Always ensure main map is included (defensive fix for race condition)
    // The main map should always be visible by definition
    if (mainMap && !maps.some((m) => m.uuid === mainMap.uuid)) {
      console.warn(
        `[Map3RelativesWrapper] Main map not in visible maps, adding defensively. uuid: ${mainMap.uuid}, visibleMapUuids: ${visibleMapUuids.size}`,
      );
      return [mainMap, ...maps];
    }
    return maps;
  }, [allMaps, visibleMapUuids, mainMap]);

  // visibleDependencies now comes from the hook

  // Filter objectives based on display mode BEFORE memoization
  const filteredObjectives = useMemo(() => {
    // If show_all mode, return all objectives without filtering
    if (objectiveDisplayMode === 'show_all') {
      return allObjectives;
    }

    // For show_with_dependencies and show_all_relatives modes, filter objectives
    // We need to filter objectives PER MAP to maintain proper associations
    const objectiveUuidsToKeep = new Set<string>();

    // For each visible map, filter its objectives and collect the UUIDs to keep
    visibleMaps.forEach((map) => {
      const mapObjectives = getOrderedObjectives(map, allObjectives);
      const filteredMapObjectives = filterMapObjectives(
        mapObjectives,
        objectiveDisplayMode,
        visibleDependencies,
        visibleMapsDependencies,
      );

      // Add filtered objective UUIDs to the set
      filteredMapObjectives.forEach((obj) => {
        objectiveUuidsToKeep.add(obj.uuid);
      });
    });

    // Return only the objectives that should be visible
    return allObjectives.filter((obj) => objectiveUuidsToKeep.has(obj.uuid));
  }, [
    allObjectives,
    objectiveDisplayMode,
    visibleMaps,
    visibleDependencies,
    visibleMapsDependencies,
  ]);

  // Memoize cloned data to prevent unnecessary re-renders
  // Only recalculate when the underlying data actually changes
  const memoizedVisibleMaps = useMemo(() => cloneDeep(visibleMaps), [visibleMaps]);
  // Use filtered objectives instead of all objectives
  const memoizedAllObjectives = useMemo(() => cloneDeep(filteredObjectives), [filteredObjectives]);
  const memoizedVisibleDependencies = useMemo(
    () => cloneDeep(visibleDependencies),
    [visibleDependencies],
  );
  const memoizedSettings = useMemo(() => cloneDeep(settings), [settings]);

  /**
   * THROTTLED REDUX DISPATCH SYSTEM
   *
   * Problem: React Flow drag operations in Map3RelativesSharedTimeline were causing runaway
   * Redux dispatches. When a node is dragged:
   * 1. onQueueResultDeadlineUpdate dispatches to Redux
   * 2. Redux updates allObjectives
   * 3. Memoization breaks, causing re-render
   * 4. useEffect runs again, regenerating nodes
   * 5. This triggers more drag events → infinite loop
   *
   * Solution: Queue-based throttling system similar to Map3DependenciesView.tsx:
   * 1. onQueueResultDeadlineUpdate queues changes instead of dispatching immediately
   * 2. A setInterval processes the queue every 250ms
   * 3. Each result can only dispatch once per 500ms (throttling)
   * 4. This prevents runaway dispatches while maintaining responsiveness
   *
   * Benefits:
   * - Immediate UI feedback (nodes move instantly)
   * - Persistent positions (no snap-back)
   * - Batched Redux updates for performance
   * - Prevents infinite re-render loops
   */

  // Throttling state: tracks last dispatch time per change to enforce THROTTLE_MS delay
  const lastDispatchTimestampsRef = useRef<{ [key: string]: number }>({});

  // Pending changes queue: holds result updates waiting to be dispatched to Redux
  type QueuedChange = {
    type: 'resultDeadline' | 'addDependency' | 'removeDependency';
    objectiveUuid?: string;
    newDeadline?: string;
    sourceObjectiveUuid?: string;
    sourceResultUuid?: string;
    targetResultUuid?: string;
    targetObjectiveUuid?: string;
    targetMapUuid?: string;
  };

  const pendingChangesRef = useRef<{ [key: string]: QueuedChange }>({});

  // Throttle delay: minimum time between Redux dispatches for the same result (500ms)
  const THROTTLE_MS = 500; // Slightly longer than Map3DependenciesView to reduce dispatch frequency

  /**
   * Queue Processing Effect
   *
   * This effect manages the throttled dispatch system to prevent runaway Redux updates
   * during drag operations. It:
   * 1. Sets up an interval to process queued changes
   * 2. Checks if enough time has passed since last dispatch for each change
   * 3. Batches and dispatches ready changes to Redux
   * 4. Cleans up the interval on unmount
   *
   * WHEN THIS RUNS:
   * - On component mount: Sets up the interval timer
   * - When allObjectives changes: Clears old interval, starts new one (dependency array)
   * - When dispatch changes: Clears old interval, starts new one (dependency array)
   *
   * WHAT THE INTERVAL DOES:
   * - Runs every 250ms (THROTTLE_MS / 2) to check for pending changes
   * - For each pending change, checks if enough time has passed since last dispatch
   * - If throttle period elapsed, dispatches the change to Redux and removes from queue
   * - If not enough time passed, leaves in queue for next interval check
   *
   * WHY setInterval INSTEAD OF JUST useEffect:
   * - useEffect would only run on dependency changes, missing queued updates
   * - setInterval ensures regular processing even when no React updates occur
   * - Cleanup function prevents memory leaks when component unmounts
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const toDispatch: QueuedChange[] = [];

      // Check each queued change to see if it's ready for dispatch
      Object.entries(pendingChangesRef.current).forEach(([key, change]) => {
        const lastDispatch = lastDispatchTimestampsRef.current[key] || 0;

        // Only dispatch if enough time has passed since last dispatch for this change
        if (now - lastDispatch > THROTTLE_MS) {
          toDispatch.push(change);
          // Remove from pending queue since we're about to dispatch it
          delete pendingChangesRef.current[key];
          // Record dispatch time to enforce throttling for future changes
          lastDispatchTimestampsRef.current[key] = now;
        }
        // If not enough time passed, leave in queue for next interval check
      });

      // Batch dispatch all ready changes to Redux
      // Dispatch to Redux - this will eventually trigger re-renders, but throttling prevents loops
      toDispatch.forEach((change) => {
        switch (change.type) {
          case 'resultDeadline':
            if (!change.objectiveUuid || !change.newDeadline || !change.sourceResultUuid) {
              console.error('Missing required fields for resultDeadline update:', change);
              return;
            }
            const objective = allObjectives.find((obj) => obj.uuid === change.objectiveUuid);
            if (!objective) {
              console.error('Objective not found for result:', change.objectiveUuid);
              return;
            }
            const updatedResults = objective.results.map((r) =>
              r.uuid === change.sourceResultUuid
                ? createResult1({ ...r, deadline: change.newDeadline })
                : r,
            );
            dispatch(
              updateObjective({
                uuid: change.objectiveUuid,
                updates: { results: updatedResults },
              }),
            ).catch((error: unknown) => {
              console.error('Failed to update result deadline:', error);
            });
            break;

          case 'addDependency':
            if (
              !change.sourceObjectiveUuid ||
              !change.sourceResultUuid ||
              !change.targetResultUuid ||
              !change.targetObjectiveUuid ||
              !change.targetMapUuid
            ) {
              console.error('Missing required fields for addDependency:', change);
              return;
            }
            dispatch(
              addResultDependency({
                sourceObjectiveUuid: change.sourceObjectiveUuid,
                sourceResultUuid: change.sourceResultUuid,
                targetResultUuid: change.targetResultUuid,
                targetObjectiveUuid: change.targetObjectiveUuid,
                targetMapUuid: change.targetMapUuid,
              }),
            ).catch((error: unknown) => {
              console.error('Failed to add dependency:', error);
            });
            break;

          case 'removeDependency':
            if (
              !change.sourceObjectiveUuid ||
              !change.sourceResultUuid ||
              !change.targetResultUuid
            ) {
              console.error('Missing required fields for removeDependency:', change);
              return;
            }
            const sourceObjective = allObjectives.find(
              (obj) => obj.uuid === change.sourceObjectiveUuid,
            );
            if (!sourceObjective) {
              console.error('Source objective not found:', change.sourceObjectiveUuid);
              return;
            }
            const sourceResult = sourceObjective.results.find(
              (r) => r.uuid === change.sourceResultUuid,
            );
            if (!sourceResult) {
              console.error('Source result not found:', change.sourceResultUuid);
              return;
            }
            const updatedResult = {
              ...sourceResult,
              dependencies: sourceResult.dependencies?.filter(
                (dep) => dep.targetResultUuid !== change.targetResultUuid,
              ),
            };
            const updatedResultsForRemove = sourceObjective.results.map((r) =>
              r.uuid === change.sourceResultUuid ? updatedResult : r,
            );
            dispatch(
              updateObjective({
                uuid: change.sourceObjectiveUuid,
                updates: {
                  results: updatedResultsForRemove,
                },
              }),
            ).catch((error: unknown) => {
              console.error('Failed to remove dependency:', error);
            });
            break;
        }
      });
    }, THROTTLE_MS / 2); // Check queue twice as often as throttle period for responsiveness

    return () => clearInterval(interval);
  }, [allObjectives, dispatch]);

  /**
   * Queues result deadline changes instead of dispatching immediately
   *
   * Called by: useNodesChangeHandler when a node is dropped (drag end)
   *
   * Instead of dispatching directly to Redux (which caused runaway updates),
   * this adds the change to pendingChangesRef queue. The setInterval effect
   * above will process it when the throttle period has elapsed.
   *
   * @param resultUuid - UUID of the result being moved
   * @param newDeadline - New deadline date (ISO string) from the dropped column
   */
  function onQueueResultDeadlineUpdate(resultUuid: string, newDeadline: string) {
    // Find the objective containing this result
    const objective = allObjectives.find((obj) => obj.results.some((r) => r.uuid === resultUuid));
    if (!objective) {
      console.error('Objective not found for result:', resultUuid);
      return;
    }

    // Queue the change for throttled dispatch
    pendingChangesRef.current[resultUuid] = {
      type: 'resultDeadline',
      objectiveUuid: objective.uuid,
      sourceResultUuid: resultUuid,
      newDeadline,
    };
  }

  /**
   * Helper to generate a unique key for dependency queue entries
   */
  function getDependencyQueueKey(
    type: 'add' | 'remove',
    sourceResultUuid: string,
    targetResultUuid: string,
  ) {
    return `${type}--source-${sourceResultUuid}--target-${targetResultUuid}`;
  }

  /**
   * Queues dependency addition instead of dispatching immediately
   */
  function onQueueDependencyAdd(sourceResultUuid: string, targetResultUuid: string) {
    try {
      // Find the source result's objective
      const sourceObjective = memoizedAllObjectives.find((obj) =>
        obj.results.some((r) => r.uuid === sourceResultUuid),
      );
      if (!sourceObjective) {
        console.error('Source objective not found for result:', sourceResultUuid);
        return;
      }

      // Find the target result's objective and map
      const targetObjective = memoizedAllObjectives.find((obj) =>
        obj.results.some((r) => r.uuid === targetResultUuid),
      );
      if (!targetObjective) {
        console.error('Target objective not found for result:', targetResultUuid);
        return;
      }

      // Find the target result's map
      const targetMap = memoizedVisibleMaps.find((map) =>
        map.objectiveUuids.includes(targetObjective.uuid),
      );
      if (!targetMap) {
        console.error('Target map not found for objective:', targetObjective.uuid);
        return;
      }

      // Queue the add dependency action
      pendingChangesRef.current[getDependencyQueueKey('add', sourceResultUuid, targetResultUuid)] =
        {
          type: 'addDependency',
          sourceObjectiveUuid: sourceObjective.uuid,
          sourceResultUuid,
          targetResultUuid,
          targetObjectiveUuid: targetObjective.uuid,
          targetMapUuid: targetMap.uuid,
        };
    } catch (error) {
      console.error('Failed to queue dependency add:', error);
    }
  }

  /**
   * Queues dependency removal instead of dispatching immediately
   */
  function onQueueDependencyRemove(sourceResultUuid: string, targetResultUuid: string) {
    try {
      // Find the source result's objective
      const sourceObjective = memoizedAllObjectives.find((obj) =>
        obj.results.some((r) => r.uuid === sourceResultUuid),
      );
      if (!sourceObjective) {
        console.error('Source objective not found for result:', sourceResultUuid);
        return;
      }

      // Find the source result
      const sourceResult = sourceObjective.results.find((r) => r.uuid === sourceResultUuid);
      if (!sourceResult) {
        console.error('Source result not found:', sourceResultUuid);
        return;
      }

      // Queue the remove dependency action
      pendingChangesRef.current[
        getDependencyQueueKey('remove', sourceResultUuid, targetResultUuid)
      ] = {
        type: 'removeDependency',
        sourceObjectiveUuid: sourceObjective.uuid,
        sourceResultUuid,
        targetResultUuid,
      };
    } catch (error) {
      console.error('Failed to queue dependency remove:', error);
    }
  }

  // Loading guard: Don't render until data is ready.
  // mr-ncj5: Also guard on !mainMap to prevent a stale-data race where
  // allRelatedMilestoneMaps.length > 0 (old map's data) but mapShortId has
  // already changed to a new map, so mainMap = find(...) returns undefined and
  // effectiveZoomLevel becomes undefined, crashing Map3Relatives:353.
  // The hidden-sibling Outlet pattern (mr-aqs.17.2) mounts this component
  // unconditionally, making the race window much wider than before PR #410.
  if (loading || !allRelatedMilestoneMaps.length || !mainMap) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="text-gray-500">Loading relatives view...</div>
      </div>
    );
  }

  return (
    <Map3Relatives
      mainMilestoneMapUuid={mainMilestoneMapUuid}
      viewMode={viewMode}
      zoomLevel={effectiveZoomLevel}
      objectiveDisplayMode={objectiveDisplayMode}
      // Use memoized cloned data to prevent render loops
      // This prevents accidental mutations of Redux state when React Flow updates
      // node positions, edges, or other visual elements
      relatedMilestoneMaps={memoizedVisibleMaps}
      allObjectives={memoizedAllObjectives}
      dependencyRecords={memoizedVisibleDependencies}
      settings={memoizedSettings}
      queueResultDeadlineUpdate={onQueueResultDeadlineUpdate}
      queueDependencyAdd={onQueueDependencyAdd}
      queueDependencyRemove={onQueueDependencyRemove}
      canEdit={canEdit}
    />
  );
}
